import { filter } from "lodash";
import { useState, useEffect, useContext } from "react";
// material
import {
  Card,
  Table,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  Stack,
  Avatar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
// components
import Page from "../components/Page";
import Scrollbar from "../components/Scrollbar";
import SearchNotFound from "../components/SearchNotFound";
import {
  TableTitle,
  TableHeader,
} from "../components/_dashboard/user";
//
import { useTranslation } from "react-i18next";
import { db } from "../database/db";
import { collection, query, getDocs, orderBy, limit, doc, getDoc } from "firebase/firestore";
import { SearchContext } from "../Context/searchContext";

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderByField) {
  if (b[orderByField] < a[orderByField]) {
    return -1;
  }
  if (b[orderByField] > a[orderByField]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderByField) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderByField)
    : (a, b) => -descendingComparator(a, b, orderByField);
}

function applySortFilter(array, comparator, queryText) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (queryText) {
    return filter(
      array,
      (_tx) => 
        (_tx.senderName?.toLowerCase().indexOf(queryText.toLowerCase()) !== -1) ||
        (_tx.receiverName?.toLowerCase().indexOf(queryText.toLowerCase()) !== -1)
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function Ledger() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("desc");
  const [selected, setSelected] = useState([]);
  const [orderByField, setOrderByField] = useState("timestamp");
  const [search, setSearch] = useContext(SearchContext);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  const TABLE_HEAD = [
    { id: "timestamp", label: "Date", alignRight: false },
    { id: "sender", label: "Sender", alignRight: false },
    { id: "receiver", label: "Receiver", alignRight: false },
    { id: "amount", label: "Amount (Tokens)", alignRight: false },
    { id: "type", label: "Type", alignRight: false },
    { id: "stream", label: "Source", alignRight: false },
  ];

  useEffect(() => {
    fetchLedger();
  }, []);

  const fetchLedger = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "Transactions"), orderBy("timestamp", "desc"), limit(100));
      const querySnapshot = await getDocs(q);
      const list = [];
      
      const userCache = {};

      const getName = async (uid) => {
        if (!uid) return "System";
        if (userCache[uid]) return userCache[uid];
        const userDoc = await getDoc(doc(db, "User", uid));
        const name = userDoc.exists() ? userDoc.data().name : "Unknown";
        userCache[uid] = name;
        return name;
      };

      for (const docSnapshot of querySnapshot.docs) {
        const data = docSnapshot.data();
        const senderName = await getName(data.fromId);
        const receiverName = await getName(data.toId);
        
        list.push({
          id: docSnapshot.id,
          senderName,
          receiverName,
          date: data.timestamp?.toDate().toLocaleString() || "N/A",
          ...data
        });
      }
      setTransactions(list);
    } catch (error) {
      console.error("Error fetching ledger:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderByField === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderByField(property);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - transactions.length) : 0;

  const filteredTransactions = applySortFilter(
    transactions,
    getComparator(order, orderByField),
    search
  );

  const isNotFound = filteredTransactions.length === 0 && !loading;

  const TableCellStyles = styled((props) => <TableCell {...props} />)(
    ({ theme }) => ({
      border: "1px solid #e3e6f0",
      fontSize: "12px",
    })
  );

  return (
    <Page title="Finance | Ledger">
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h3" sx={{ color: "info.main" }}>
            Financial Ledger
          </Typography>
        </Stack>

        <Card sx={{ borderRadius: "5px" }}>
          <TableTitle title="Token Transaction History" />
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, p: "18px" }}>
              <Table>
                <TableHeader
                  order={order}
                  orderBy={orderByField}
                  headLabel={TABLE_HEAD}
                  rowCount={transactions.length}
                  numSelected={0}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {filteredTransactions
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => {
                      const {
                        id,
                        date,
                        senderName,
                        receiverName,
                        amount,
                        type,
                        streamTitle,
                      } = row;

                      return (
                        <TableRow hover key={id} tabIndex={-1}>
                          <TableCellStyles align="left">{date}</TableCellStyles>
                          <TableCellStyles align="left">{senderName}</TableCellStyles>
                          <TableCellStyles align="left">{receiverName}</TableCellStyles>
                          <TableCellStyles align="left" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            {amount}
                          </TableCellStyles>
                          <TableCellStyles align="left">{type?.toUpperCase()}</TableCellStyles>
                          <TableCellStyles align="left">{streamTitle || "N/A"}</TableCellStyles>
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCellStyles colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCellStyles align="center" colSpan={6} sx={{ py: 3 }}>
                        <SearchNotFound />
                      </TableCellStyles>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
        </Card>
      </Container>
    </Page>
  );
}
